import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import useAuth from "hooks/useAuth";
import {
  CONTEXT_TOP_NAV_MODULES,
  isModuleAllowedForLevel,
} from "config/contextModules";
import { useLocationContext } from "context/LocationContext";

export default function FunctionalTopNav({ onOpenLocationNav }) {
  const navigate = useNavigate();
  const { dispatch, user } = useAuth();
  const {
    assessmentUsers,
    currentLocation,
    currentModuleSlug,
    goToModule,
    isAssessmentUsersLoading,
    isOrgTreeLoading,
  } = useLocationContext();
  const level = currentLocation?.level || "national";
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const fullName = user?.user?.fullName || "User";
  const initials = fullName.trim().charAt(0).toUpperCase();
  const roleSummary = useMemo(() => {
    const roleNames = (user?.user?.userRoles || [])
      .map((role) => String(role?.name || "").trim())
      .filter(Boolean);

    if (roleNames.length === 0) return "No role assigned";
    if (roleNames.length === 1) return roleNames[0];
    return `${roleNames[0]} +${roleNames.length - 1}`;
  }, [user]);
  const visibleModules = useMemo(
    () => {
      if (
        isOrgTreeLoading
        || isAssessmentUsersLoading
        || currentLocation?.hasAccess === false
      ) return [];

      return CONTEXT_TOP_NAV_MODULES.filter(
        (moduleConfig) =>
          !moduleConfig.hideFromTopNav &&
          isModuleAllowedForLevel(
            moduleConfig.slug,
            level,
            user,
            assessmentUsers,
            currentLocation,
          ),
      );
    },
    [
      assessmentUsers,
      currentLocation?.hasAccess,
      currentLocation?.id,
      isAssessmentUsersLoading,
      isOrgTreeLoading,
      level,
      user,
    ],
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isProfileMenuOpen]);

  const handleLogout = () => {
    setIsProfileMenuOpen(false);
    dispatch({ type: "LOGOUT" });
    navigate("/login");
  };

  const handleSettingsNavigation = () => {
    setIsProfileMenuOpen(false);
    goToModule("settings");
  };

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-card shadow-sm">
      <div className="px-5 py-3 md:px-6">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onOpenLocationNav}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-muted md:hidden"
            aria-label="Open location navigation"
          >
            <Icon name="Menu" className="h-5 w-5" />
          </button>
          <nav className="overflow-x-auto flex-1 min-w-0">
              <div className="inline-flex min-w-full sm:min-w-0 gap-2">
              {visibleModules.map((moduleConfig) => {
                const isActive = currentModuleSlug === moduleConfig.slug;

                return (
                  <button
                    key={moduleConfig.slug}
                    type="button"
                    onClick={() => goToModule(moduleConfig.slug)}
                    title={moduleConfig.description}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors whitespace-nowrap inline-flex items-center gap-2 ${
                      isActive
                        ? "border-[#16325a] bg-[#16325a] text-white"
                        : "bg-background text-foreground border-border hover:bg-muted"
                    }`}
                  >
                    <Icon name={moduleConfig.icon} className="w-4 h-4" />
                    {moduleConfig.label}
                  </button>
                );
              })}
            </div>
          </nav>
          <div className="relative shrink-0" ref={profileMenuRef}>
            <button
              type="button"
              onClick={() => setIsProfileMenuOpen((prev) => !prev)}
              className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold transition-colors ${
                isProfileMenuOpen
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-border bg-background text-foreground hover:bg-muted"
              }`}
              title="Open user menu"
              aria-haspopup="menu"
              aria-expanded={isProfileMenuOpen}
            >
              {initials}
            </button>

            <div className={`dropdown-menu ${isProfileMenuOpen ? "open" : ""}`}>
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-semibold text-foreground truncate">{fullName}</p>
                <p className="text-xs text-muted-foreground truncate">{roleSummary}</p>
              </div>

              <button type="button" onClick={handleSettingsNavigation} className="dropdown-item w-full text-left flex items-center gap-2">
                <Icon name="Settings" className="h-4 w-4" />
                User Settings
              </button>

              <button type="button" onClick={handleLogout} className="dropdown-item w-full text-left flex items-center gap-2 text-destructive">
                <Icon name="LogOut" className="h-4 w-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
